import { DateRange } from '@mui/lab';
import { formatISO } from 'date-fns';
import { useIntl } from 'react-intl';
import { CloseGrayIcon, PaginationSelectIcon, Select } from 'shared-ui';

import { EventCategoryEnum, EventModEnum } from 'api/events/events.enum';
import { InneEventsQueryKeysEnum, QueryKeyLocalStorageEnum } from 'context/query/queryContext/QueryContext.enum';
import { filterQueryFromPagination } from 'helpers/filterQueryFromPagination';
import { useAppSelectOptions } from 'hooks/useAppSelectOptions/useAppSelectOptions';
import { useLocaleStoreQuery } from 'hooks/useLocaleStoreQuery/useLocaleStoreQuery';
import { AppMessages } from 'i18n/messages';
import { DownloadTableContainer } from 'reusable/downloadTable/DownloadTableContainer';
import { HistoryDatepickerWithModal } from 'reusable/historyDatepickerWithModal/HistoryDatepickerWithModal';
import { TableSearchContainer } from 'reusable/tableSearch/TableSearchContainer';

import * as Styled from './InneEventsFilters.styles';
import { InneEventsFiltersProps } from './InneEventsFilters.types';

export const InneEventsFilters = ({
  servers,
  setTableSearchQuery,
  tableSearchQuery,
  tkdSearchQuery,
  setTkdSearchQuery,
}: InneEventsFiltersProps) => {
  const {
    setQuery: setInneEventsQuery,
    storedQueries: { [QueryKeyLocalStorageEnum.EVENTS_OTHER]: inneEventsQuery },
  } = useLocaleStoreQuery();
  const { formatMessage } = useIntl();
  const { modOptions, categoryOptions, serverOptions } = useAppSelectOptions({ servers });

  const handleInneEventsQueryChange = (query: typeof inneEventsQuery) => {
    setInneEventsQuery(QueryKeyLocalStorageEnum.EVENTS_OTHER, query);
  };

  const isClearFiltersButtonVisible =
    Object.values(filterQueryFromPagination(inneEventsQuery, ['sort'])).some((query) => query !== undefined) ||
    tableSearchQuery;

  const handleSetDates = (dates: DateRange<Date>) => {
    handleInneEventsQueryChange({
      ...inneEventsQuery,
      dataOd: dates[0] ? formatISO(dates[0]) : undefined,
      dataDo: dates[1] ? formatISO(dates[1]) : undefined,
    });
  };

  const onClearInneEventsQuery = () => {
    handleInneEventsQueryChange({
      page: inneEventsQuery.page,
      rowsPerPage: inneEventsQuery.rowsPerPage,
    });
    setTableSearchQuery('');
  };

  const createSelectIcons = (props: Record<string, unknown>, queryKey: InneEventsQueryKeysEnum) =>
    inneEventsQuery[queryKey] !== undefined ? (
      <Styled.RemoveIconWrapper
        {...props}
        onClick={() =>
          handleInneEventsQueryChange({
            ...inneEventsQuery,
            [queryKey]: undefined,
            [InneEventsQueryKeysEnum.CZYTNIK]:
              queryKey === InneEventsQueryKeysEnum.SERVER ? undefined : inneEventsQuery.tkd,
          })
        }
      >
        <CloseGrayIcon />
      </Styled.RemoveIconWrapper>
    ) : (
      <Styled.SelectIconWrapper {...props}>
        <PaginationSelectIcon />
      </Styled.SelectIconWrapper>
    );

  return (
    <Styled.Filters>
      <HistoryDatepickerWithModal
        dates={[
          inneEventsQuery.dataOd ? new Date(inneEventsQuery.dataOd) : null,
          inneEventsQuery.dataDo ? new Date(inneEventsQuery.dataDo) : null,
        ]}
        setDates={handleSetDates}
      />

      {!!servers.length && (
        <Select
          fullWidth
          variant="outlined"
          onChange={(e) =>
            handleInneEventsQueryChange({
              ...inneEventsQuery,
              [InneEventsQueryKeysEnum.SERVER]: e.target.value as string,
            })
          }
          value={inneEventsQuery[InneEventsQueryKeysEnum.SERVER] || ''}
          IconComponent={(props) => createSelectIcons(props, InneEventsQueryKeysEnum.SERVER)}
          placeholder={formatMessage({ id: AppMessages['events.other.filters.server.placeholder'] })}
          options={serverOptions}
        />
      )}

      {!!servers.length && (
        <TableSearchContainer
          withoutToggle
          inputPlaceholder={formatMessage({ id: AppMessages['announcements.history.filters.reader'] })}
          searchBy="terminals"
          tableSearchQuery={tkdSearchQuery}
          setTableSearchQuery={setTkdSearchQuery}
          selectedValue={inneEventsQuery[InneEventsQueryKeysEnum.CZYTNIK]}
          setSelectedValue={(option) =>
            handleInneEventsQueryChange({
              ...inneEventsQuery,
              [InneEventsQueryKeysEnum.CZYTNIK]: option || undefined,
            })
          }
          inputSize="big"
          isDisabled={!inneEventsQuery.serwer}
          serverId={inneEventsQuery.serwer}
        />
      )}

      <Select
        fullWidth
        variant="outlined"
        onChange={(e) =>
          handleInneEventsQueryChange({
            ...inneEventsQuery,
            [InneEventsQueryKeysEnum.MOD]: e.target.value as EventModEnum,
          })
        }
        value={inneEventsQuery[InneEventsQueryKeysEnum.MOD] || ''}
        IconComponent={(props) => createSelectIcons(props, InneEventsQueryKeysEnum.MOD)}
        placeholder={formatMessage({ id: AppMessages['events.filters.mod.placeholder'] })}
        options={modOptions}
      />

      <Select
        fullWidth
        variant="outlined"
        onChange={(e) =>
          handleInneEventsQueryChange({
            ...inneEventsQuery,
            [InneEventsQueryKeysEnum.KATEGORIA]: e.target.value as EventCategoryEnum,
          })
        }
        value={inneEventsQuery[InneEventsQueryKeysEnum.KATEGORIA] || ''}
        IconComponent={(props) => createSelectIcons(props, InneEventsQueryKeysEnum.KATEGORIA)}
        placeholder={formatMessage({ id: AppMessages['events.other.filters.category.placeholder'] })}
        options={categoryOptions}
      />

      <Styled.SearchWrapper>
        <TableSearchContainer
          withoutToggle
          inputSize="big"
          searchBy="cardsAndOwners"
          tableSearchQuery={tableSearchQuery}
          setTableSearchQuery={setTableSearchQuery}
          inputPlaceholder={formatMessage({ id: AppMessages['events.other.filters.search.placeholder'] })}
        />
      </Styled.SearchWrapper>

      <Styled.ActionsWrapper>
        <DownloadTableContainer type="inneEvents" query={{ ...inneEventsQuery, query: tableSearchQuery }} />
        {isClearFiltersButtonVisible && (
          <Styled.ClearFiltersWrapper>
            <Styled.ClearFiltersButton onClick={onClearInneEventsQuery}>
              {formatMessage({ id: AppMessages['common.clear'] })}
              <Styled.ClearFiltersIcon />
            </Styled.ClearFiltersButton>
          </Styled.ClearFiltersWrapper>
        )}
      </Styled.ActionsWrapper>
    </Styled.Filters>
  );
};
